/**
 * tfh-qty-cap.js
 * Top Flight Hockey — Quantity Cap + Stock Display
 *
 * - Sets the quantity input max to the available inventory for the selected variant
 * - Disables the + button when at max
 * - Shows an "X available" badge near the quantity selector
 * - Updates automatically when the user switches variants
 *
 * Install:
 *   1. Shopify Admin → Online Store → Themes → Edit code (live theme)
 *   2. Assets → Add a new asset → Blank file → name it "tfh-qty-cap.js" → paste this file
 *   3. In sections/main-product.liquid, just before the closing </product-info> tag, add:
 *      <script src="{{ 'tfh-qty-cap.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  // ── Build a variant inventory map from the embedded product JSON ─────────
  // Shopify embeds product data in a <script type="application/json"> block
  // inside product-info, but the most reliable source is window.ShopifyAnalytics
  // or the product JSON endpoint. We use the /products/{handle}.js endpoint
  // so we always get live quantities without relying on embedded snapshots.

  function getProductHandle() {
    // Dawn sets data-url on <product-info>
    const el = document.querySelector('product-info[data-url]');
    if (!el) return null;
    // data-url is like /products/some-handle
    const match = el.getAttribute('data-url').match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }

  async function fetchVariantInventory(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return {};
      const data = await res.json();
      const map = {};
      for (const v of data.variants || []) {
        // inventory_quantity is available on the storefront product JSON
        map[v.id] = v.inventory_quantity != null ? v.inventory_quantity : null;
      }
      return map;
    } catch (e) {
      console.warn('[tfh-qty-cap] Could not fetch product inventory', e);
      return {};
    }
  }

  // ── DOM helpers ────────────────────────────────────────────────────────────

  function getQtyInput() {
    return document.querySelector('quantity-input .quantity__input');
  }

  function getPlusButton() {
    return document.querySelector('quantity-input .quantity__button[name="plus"]');
  }

  // Find or create the stock badge element
  function getOrCreateBadge() {
    let badge = document.getElementById('tfh-stock-badge');
    if (badge) return badge;

    badge = document.createElement('p');
    badge.id = 'tfh-stock-badge';
    badge.style.cssText = [
      'margin: 4px 0 0 0',
      'font-size: 12px',
      'font-weight: 600',
      'letter-spacing: 0.08em',
      'text-transform: uppercase',
      'color: var(--tfh-ice, #7dd3fc)',
      'font-family: var(--font-heading-family, Quantico, sans-serif)',
    ].join(';');

    // Insert after the quantity-input wrapper
    const qtyWrapper = document.querySelector('#Quantity-Form-{{ section.id }}, [id^="Quantity-Form-"]');
    if (qtyWrapper) {
      qtyWrapper.insertAdjacentElement('afterend', badge);
    } else {
      // Fallback: after the buy buttons
      const buyBtn = document.querySelector('.product-form__buttons');
      if (buyBtn) buyBtn.insertAdjacentElement('beforebegin', badge);
    }
    return badge;
  }

  function applyInventoryToUI(qty) {
    const input = getQtyInput();
    const plus = getPlusButton();
    const badge = getOrCreateBadge();

    if (!input) return;

    if (qty == null) {
      // Inventory not tracked — remove cap and badge
      input.removeAttribute('max');
      input.removeAttribute('data-max');
      if (plus) plus.removeAttribute('disabled');
      badge.textContent = '';
      return;
    }

    if (qty <= 0) {
      // Out of stock (shouldn't normally show on storefront, but handle gracefully)
      input.setAttribute('max', '0');
      input.setAttribute('data-max', '0');
      if (plus) plus.setAttribute('disabled', 'disabled');
      badge.textContent = 'Out of stock';
      badge.style.color = 'var(--tfh-text-dim, rgba(255,255,255,0.4))';
      return;
    }

    // Set the cap
    input.setAttribute('max', String(qty));
    input.setAttribute('data-max', String(qty));

    // Clamp current value if it exceeds max
    const current = parseInt(input.value, 10);
    if (!isNaN(current) && current > qty) {
      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Enable/disable + button based on current value
    updatePlusButton(input, plus, qty);

    // Show badge
    const urgencyThreshold = 5;
    if (qty <= urgencyThreshold) {
      badge.textContent = `Only ${qty} left`;
      badge.style.color = '#e2a000'; // amber urgency tone
    } else {
      badge.textContent = `${qty} available`;
      badge.style.color = 'var(--tfh-ice, #7dd3fc)';
    }
  }

  function updatePlusButton(input, plus, max) {
    if (!plus) return;
    const current = parseInt(input.value, 10);
    if (!isNaN(current) && current >= max) {
      plus.setAttribute('disabled', 'disabled');
    } else {
      plus.removeAttribute('disabled');
    }
  }

  // ── Main init ──────────────────────────────────────────────────────────────

  async function init() {
    const handle = getProductHandle();
    if (!handle) return;

    const inventoryMap = await fetchVariantInventory(handle);

    function applyForVariant(variantId) {
      const qty = inventoryMap[variantId];
      applyInventoryToUI(qty !== undefined ? qty : null);
    }

    // Get the initially-selected variant ID from the hidden input Dawn uses
    function getSelectedVariantId() {
      const hiddenInput = document.querySelector('input[name="id"][form^="product-form-"]');
      return hiddenInput ? parseInt(hiddenInput.value, 10) : null;
    }

    // Apply on load
    const initialId = getSelectedVariantId();
    if (initialId) applyForVariant(initialId);

    // Re-apply whenever Dawn fires variant:change (triggered by product-info.js)
    document.addEventListener('variant:change', function (e) {
      const variantId = e.detail && e.detail.variant && e.detail.variant.id;
      if (variantId) applyForVariant(variantId);
    });

    // Also listen for changes on the hidden variant input directly (fallback)
    const hiddenInput = document.querySelector('input[name="id"][form^="product-form-"]');
    if (hiddenInput) {
      hiddenInput.addEventListener('change', function () {
        const id = parseInt(this.value, 10);
        if (id) applyForVariant(id);
      });
    }

    // Re-check + button state when qty input changes manually
    document.addEventListener('input', function (e) {
      if (!e.target.classList.contains('quantity__input')) return;
      const input = e.target;
      const max = parseInt(input.getAttribute('max'), 10);
      if (!isNaN(max)) {
        updatePlusButton(input, getPlusButton(), max);
      }
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();